import type {Component, Editor} from 'grapesjs';
import type {CropData} from '@Panels/cropper-panel/cropper-panel.types';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getStringProp, isMediaComponentLoading} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import {setImageCropData} from '@EmailMakerReact/lib/email-maker-media-library';
import {getStoredCropData} from '@EmailMakerReact/lib/email-maker-crop-data-library';
import {canCropMediaUrl} from '@EmailMakerReact/lib/email-maker-cropped-media-url-library';
import {doesMediaComponentHaveUserAsset} from '@EmailMakerReact/lib/email-maker-overlay-library';
import {attachImageCropKeyboard} from '@EmailMakerReact/lib/email-maker-image-crop-keyboard-library';
import {lockComponentForImageCrop, unlockComponentAfterImageCrop} from '@EmailMakerReact/lib/email-maker-image-crop-canvas-lock-library';
import type {ComponentInteractionProps} from '@EmailMakerReact/lib/email-maker-image-crop-canvas-lock-library';
import {clampCropRectToImage, fitCropRectToAspectRatio, getFullImageCropRect, scaleCropRectBetweenImageSizes} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';
import type {CropRect, ImageSize} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';

export interface EmailImageCropModeState {
  component: Component;
  imageSize: ImageSize;
  cropWindow: CropRect;
  aspectRatio: number | null;
  isOriginalAspectRatio: boolean;
}

const IMAGE_CROP_MODE_CHANGE_EVENT = 'pmw:image-crop-mode:change';
const IMAGE_CROP_CANCEL_EVENTS = 'device:select undo redo';
const SRC_PROP = 'src';

interface PendingImageCropSession {
  kind: 'pending';
  component: Component;
}

interface ActiveImageCropSession {
  kind: 'active';
  state: EmailImageCropModeState;
  detachListeners: () => void;
  previousInteractionProps: ComponentInteractionProps;
}

type ImageCropSession = PendingImageCropSession | ActiveImageCropSession;

const sessions = new WeakMap<Editor, ImageCropSession>();

export async function enterImageCropMode(editor: Editor, component: Component | undefined): Promise<void> {
  if (!component || !isComponentCroppable(component)) {
    return;
  }

  const activeState = getImageCropModeState(editor);
  if (activeState?.component === component) {
    return;
  }
  if (activeState) {
    applyImageCrop(editor);
  }

  sessions.set(editor, {kind: 'pending', component});
  const uncroppedSrc = getUncroppedSrc(component);
  let imageSize: ImageSize;
  try {
    imageSize = await loadNaturalImageSize(uncroppedSrc);
  } catch (error) {
    logEmailMakerError('enterImageCropMode: failed to load the uncropped image', error);
    if (isPendingRequest(editor, component)) {
      sessions.delete(editor);
    }
    return;
  }

  if (!isPendingRequest(editor, component)) {
    return;
  }
  sessions.delete(editor);
  if (!isComponentSelectedOnCanvas(editor, component)) {
    return;
  }
  startImageCropSession(editor, {component, imageSize, uncroppedSrc});
}

export function applyImageCrop(editor: Editor): void {
  const session = getActiveSession(editor);
  if (!session) {
    return;
  }

  const {state} = session;
  const cropData: CropData = {...state.cropWindow, imageWidth: state.imageSize.width, imageHeight: state.imageSize.height};
  exitImageCropSession(editor, session);
  setImageCropData(editor, state.component, cropData);
}

export function cancelImageCrop(editor: Editor): void {
  const session = getActiveSession(editor);
  if (!session) {
    return;
  }
  exitImageCropSession(editor, session);
}

export function setImageCropWindow(editor: Editor, cropWindow: CropRect): void {
  updateImageCropModeState(editor, (state) => ({...state, cropWindow: clampCropRectToImage(cropWindow, state.imageSize)}));
}

export function setImageCropAspectRatio(editor: Editor, ratio: number | null): void {
  updateImageCropModeState(editor, (state) => ({
    ...state,
    aspectRatio: ratio,
    isOriginalAspectRatio: false,
    cropWindow: ratio === null ? state.cropWindow : fitCropRectToAspectRatio(state.cropWindow, ratio, state.imageSize),
  }));
}

export function resetImageCropToOriginal(editor: Editor): void {
  updateImageCropModeState(editor, (state) => ({
    ...state,
    cropWindow: getFullImageCropRect(state.imageSize),
    aspectRatio: state.imageSize.width / state.imageSize.height,
    isOriginalAspectRatio: true,
  }));
}

export function getImageCropModeState(editor: Editor): EmailImageCropModeState | null {
  return getActiveSession(editor)?.state ?? null;
}

export function subscribeToImageCropMode(editor: Editor, callback: () => void): () => void {
  editor.on(IMAGE_CROP_MODE_CHANGE_EVENT, callback);
  return (): void => {
    editor.off(IMAGE_CROP_MODE_CHANGE_EVENT, callback);
  };
}

interface ImageCropSessionStart {
  component: Component;
  imageSize: ImageSize;
  uncroppedSrc: string;
}

function startImageCropSession(editor: Editor, {component, imageSize, uncroppedSrc}: ImageCropSessionStart): void {
  const element = component.getEl();
  if (!element) {
    return;
  }

  const previousInteractionProps = lockComponentForImageCrop(editor, component, element);
  element.setAttribute(SRC_PROP, uncroppedSrc);
  publishActiveSession(editor, {
    kind: 'active',
    state: {
      component,
      imageSize,
      cropWindow: getInitialCropWindow(component, imageSize),
      aspectRatio: null,
      isOriginalAspectRatio: false,
    },
    detachListeners: attachImageCropListeners(editor, component),
    previousInteractionProps,
  });
}

function exitImageCropSession(editor: Editor, session: ActiveImageCropSession): void {
  sessions.delete(editor);
  session.detachListeners();

  const {component} = session.state;
  component.getEl()?.setAttribute(SRC_PROP, getStringProp(component, SRC_PROP) ?? '');
  unlockComponentAfterImageCrop(editor, component, session.previousInteractionProps);
  editor.trigger(IMAGE_CROP_MODE_CHANGE_EVENT);
}

function attachImageCropListeners(editor: Editor, component: Component): () => void {
  const onComponentSelected = (selected: Component): void => {
    if (selected !== component) {
      applyImageCrop(editor);
    }
  };
  const onComponentDeselected = (deselected: Component): void => {
    if (deselected === component) {
      applyImageCrop(editor);
    }
  };
  const onComponentRemoved = (removed: Component): void => {
    if (removed === component) {
      cancelImageCrop(editor);
    }
  };
  const onCancelEvent = (): void => {
    cancelImageCrop(editor);
  };
  const onEditorDestroyed = (): void => {
    detachListeners();
    sessions.delete(editor);
  };
  const detachKeyboard = attachImageCropKeyboard(editor, {
    onApply: (): void => {
      applyImageCrop(editor);
    },
    onCancel: onCancelEvent,
  });

  editor.on('component:selected', onComponentSelected);
  editor.on('component:deselected', onComponentDeselected);
  editor.on('component:remove', onComponentRemoved);
  editor.on(IMAGE_CROP_CANCEL_EVENTS, onCancelEvent);
  editor.on('destroy', onEditorDestroyed);

  function detachListeners(): void {
    editor.off('component:selected', onComponentSelected);
    editor.off('component:deselected', onComponentDeselected);
    editor.off('component:remove', onComponentRemoved);
    editor.off(IMAGE_CROP_CANCEL_EVENTS, onCancelEvent);
    editor.off('destroy', onEditorDestroyed);
    detachKeyboard();
  }

  return detachListeners;
}

function getInitialCropWindow(component: Component, imageSize: ImageSize): CropRect {
  const cropData = getStoredCropData(component);
  if (!cropData) {
    return getFullImageCropRect(imageSize);
  }
  return scaleCropRectBetweenImageSizes(cropData, {width: cropData.imageWidth, height: cropData.imageHeight}, imageSize);
}

function updateImageCropModeState(editor: Editor, getNextState: (state: EmailImageCropModeState) => EmailImageCropModeState): void {
  const session = getActiveSession(editor);
  if (!session) {
    return;
  }
  publishActiveSession(editor, {...session, state: getNextState(session.state)});
}

function publishActiveSession(editor: Editor, session: ActiveImageCropSession): void {
  sessions.set(editor, session);
  editor.trigger(IMAGE_CROP_MODE_CHANGE_EVENT);
}

function getActiveSession(editor: Editor): ActiveImageCropSession | null {
  const session = sessions.get(editor);
  return session?.kind === 'active' ? session : null;
}

function isPendingRequest(editor: Editor, component: Component): boolean {
  const session = sessions.get(editor);
  return session?.kind === 'pending' && session.component === component;
}

function isComponentCroppable(component: Component): boolean {
  return (
    Boolean(component.get(EMAIL_MAKER_PROPS.IS_CROPPABLE)) &&
    doesMediaComponentHaveUserAsset(component) &&
    canCropMediaUrl(getUncroppedSrc(component)) &&
    !isMediaComponentLoading(component)
  );
}

function isComponentSelectedOnCanvas(editor: Editor, component: Component): boolean {
  return editor.getSelected() === component && component.getEl()?.isConnected === true;
}

function getUncroppedSrc(component: Component): string {
  const uncroppedSrc = getStringProp(component, EMAIL_MAKER_PROPS.UNCROPPED_SRC) ?? '';
  if (uncroppedSrc !== '') {
    return uncroppedSrc;
  }
  return getStringProp(component, SRC_PROP) ?? '';
}

function loadNaturalImageSize(src: string): Promise<ImageSize> {
  return new Promise((resolve, reject): void => {
    const image = new Image();
    image.onload = (): void => {
      if (!image.naturalWidth || !image.naturalHeight) {
        reject(new Error(`Image has no natural size ${src}`));
        return;
      }
      resolve({width: image.naturalWidth, height: image.naturalHeight});
    };
    image.onerror = (): void => {
      reject(new Error(`Could not load image ${src}`));
    };
    image.src = src;
  });
}
